/**
 * React hook for persisting user's default view configuration and session layout.
 *
 * Saved views go in localStorage (survives browser restart).
 * Session layout goes in sessionStorage (survives page reload, not browser restart).
 */

import { useState, useEffect, useCallback } from 'react';
import type { ViewConfig, ColumnLayout, Density } from '../model/types';
import { parseViewConfig, serializeViewConfig } from '../model/viewConfig';
import { useUser } from '../../../auth/hooks';

interface SessionLayout {
  layout: ColumnLayout;
  density: Density;
}

interface UseViewConfigResult {
  /** Saved default view, or null if none. */
  saved: ViewConfig | null;
  /** Save a view config as the user's default. */
  save: (cfg: ViewConfig) => void;
  /** Clear the saved default. */
  clear: () => void;
  /** Export a view config as a downloadable JSON file. */
  exportFile: (cfg: ViewConfig) => void;
  /** Import a view config from a file; throws Error on invalid file. */
  importFile: (file: File) => Promise<ViewConfig>;
  /** Current session layout, or null if none. */
  sessionLayout: SessionLayout | null;
  /** Save session layout (ephemeral, for this session only). */
  saveSession: (v: SessionLayout) => void;
  /** Clear session layout. */
  clearSession: () => void;
}

/**
 * Hook for managing user's saved default view and session layout.
 *
 * @param scope - Persistence scope (e.g. "lexisnexis-preview")
 */
export function useViewConfig(scope: string): UseViewConfigResult {
  const user = useUser();
  const sub = user?.sub || 'anonymous';

  // Storage keys
  const viewKey = `edna.explorer.view.v1:${scope}:${sub}`;
  const sessionKey = `edna.explorer.session.v1:${scope}:${sub}`;

  // Read synchronously so the first preview render already sees the user's default;
  // re-read when the signed-in user changes.
  const [saved, setSaved] = useState<ViewConfig | null>(() => readSaved(viewKey, scope));
  const [sessionLayout, setSessionLayout] = useState<SessionLayout | null>(() => readSession(sessionKey));

  useEffect(() => {
    setSaved(readSaved(viewKey, scope));
  }, [viewKey, scope]);

  useEffect(() => {
    setSessionLayout(readSession(sessionKey));
  }, [sessionKey]);

  // Save a view config
  const save = useCallback((cfg: ViewConfig) => {
    try {
      const json = serializeViewConfig(cfg);
      localStorage.setItem(viewKey, json);
      setSaved(cfg);
    } catch (error) {
      console.error('[explorer] failed to save view:', error);
      // Don't throw - gracefully degrade
    }
  }, [viewKey]);

  // Clear saved view
  const clear = useCallback(() => {
    try {
      localStorage.removeItem(viewKey);
      setSaved(null);
    } catch (error) {
      console.error('[explorer] failed to clear saved view:', error);
    }
  }, [viewKey]);

  // Export view config as a file
  const exportFile = useCallback((cfg: ViewConfig) => {
    try {
      const json = serializeViewConfig(cfg);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `edna-view-${scope}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('[explorer] failed to export view:', error);
      // Silently fail - user will notice the download didn't start
    }
  }, [scope]);

  // Import view config from a file
  const importFile = useCallback(async (file: File): Promise<ViewConfig> => {
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const validated = parseViewConfig(parsed, scope);

      if (!validated) {
        throw new Error('That file is not a valid explorer view for this page.');
      }

      // Save it
      save(validated);

      return validated;
    } catch (error) {
      // Re-throw with user-friendly message
      if (error instanceof SyntaxError) {
        throw new Error('That file is not valid JSON.');
      }
      throw error;
    }
  }, [scope, save]);

  // Save session layout
  const saveSession = useCallback((v: SessionLayout) => {
    try {
      const json = JSON.stringify(v);
      sessionStorage.setItem(sessionKey, json);
      setSessionLayout(v);
    } catch (error) {
      console.error('[explorer] failed to save session layout:', error);
    }
  }, [sessionKey]);

  // Clear session layout
  const clearSession = useCallback(() => {
    try {
      sessionStorage.removeItem(sessionKey);
      setSessionLayout(null);
    } catch (error) {
      console.error('[explorer] failed to clear session layout:', error);
    }
  }, [sessionKey]);

  return {
    saved,
    save,
    clear,
    exportFile,
    importFile,
    sessionLayout,
    saveSession,
    clearSession,
  };
}

// ---------- Helpers ----------

function isValidSessionLayout(value: unknown): value is SessionLayout {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const v = value as Record<string, unknown>;

  // Validate layout
  if (typeof v.layout !== 'object' || v.layout === null) {
    return false;
  }

  const layout = v.layout as Record<string, unknown>;

  // Check that layout has the required fields with correct types
  if (!Array.isArray(layout.order)) return false;
  if (!Array.isArray(layout.hidden)) return false;
  if (!Array.isArray(layout.pinned)) return false;
  if (!Array.isArray(layout.collapsedGroups)) return false;
  if (typeof layout.widths !== 'object' || layout.widths === null || Array.isArray(layout.widths)) {
    return false;
  }

  // Validate density
  if (v.density !== 'compact' && v.density !== 'comfortable') {
    return false;
  }

  return true;
}

function readSaved(viewKey: string, scope: string): ViewConfig | null {
  try {
    const stored = localStorage.getItem(viewKey);
    if (!stored) return null;
    const parsed = parseViewConfig(stored, scope);
    if (!parsed) {
      localStorage.removeItem(viewKey);
      console.warn('[explorer] discarded invalid saved view');
    }
    return parsed;
  } catch (error) {
    // Storage can be unavailable (private mode, policy); degrade to no saved view.
    console.warn('[explorer] failed to load saved view:', error);
    return null;
  }
}

function readSession(sessionKey: string): SessionLayout | null {
  try {
    const stored = sessionStorage.getItem(sessionKey);
    if (!stored) return null;
    const parsed: unknown = JSON.parse(stored);
    if (isValidSessionLayout(parsed)) return parsed;
    sessionStorage.removeItem(sessionKey);
    return null;
  } catch (error) {
    console.warn('[explorer] failed to load session layout:', error);
    return null;
  }
}
